import type { MessageRewriteAction } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';

const REWRITE_DELAY_MS = 1000;

const CANNED_REWRITES: Record<MessageRewriteAction, string> = {
  shorten:
    `Hi {Learner},\n\n` +
    `Your courses are still waiting. Even 15 minutes a day can make a difference.\n\n` +
    `• Continue your learning journey.\n\n` +
    `The Learning Team`,
  expand:
    `Hi {Learner},\n\n` +
    `We've noticed you haven't logged in to your Coursera learning program recently, and we wanted to reach out personally. Your enrolled courses are still available to you, and your progress is preserved exactly where you left off. Research consistently shows that even short, regular study sessions — as little as 15 minutes a day — can compound into meaningful progress over the course of a few weeks.\n\n` +
    `• Continue your learning journey at your own pace.\n` +
    `• Pick up exactly where you stopped.\n` +
    `• Earn certificates that demonstrate the skills you've built.\n\n` +
    `We're rooting for you and we're here whenever you're ready to dive back in.\n` +
    `The Learning Team`,
  more_casual:
    `Hey {Learner}!\n\n` +
    `Long time no see! Your courses miss you 👀 Even just 15 minutes a day can get you back on track — promise.\n\n` +
    `• Jump back in whenever you're ready.\n\n` +
    `Rooting for you,\n` +
    `The Learning Team`,
  more_formal:
    `Dear {Learner},\n\n` +
    `Consistent progress, even in small increments such as 15 minutes per day, is instrumental in achieving your professional development goals and ensuring the successful completion of your program. Your courses remain accessible and ready for your return.\n\n` +
    `• Continue your learning journey at your earliest convenience.\n\n` +
    `We're rooting for you,\n` +
    `The Learning Team`,
  add_urgency:
    `Hi {Learner},\n\n` +
    `Time is running out — your courses are still waiting, but progress doesn't wait forever. Even 15 minutes today can keep your momentum alive. Don't let the work you've already invested slip away.\n\n` +
    `• Continue your learning journey before you fall further behind.\n\n` +
    `The Learning Team`,
};

/**
 * Stand-in for the real "rewrite message" backend call. Resolves after a
 * short delay with a per-action canned body so the Review screen can demo
 * the full pill-click → loading → rewritten-body flow.
 *
 * The current body is accepted as input but ignored by this mock — the real
 * endpoint will be the one to decide whether rewrites chain on top of the
 * current body or always re-run against the original. The UI stays agnostic.
 *
 * Replace the body of this function with the real GraphQL mutation when the
 * endpoint ships; the call site and Promise shape stay identical.
 */
export const mockRewriteMessage = (_currentBody: string, action: MessageRewriteAction): Promise<string> => {
  return new Promise((resolve) => {
    setTimeout(() => resolve(CANNED_REWRITES[action]), REWRITE_DELAY_MS);
  });
};

export { REWRITE_DELAY_MS };
